import React, { useMemo } from 'react'

import { Table, TableRow } from '../../../../../components/pdf/Table'
import { TableColText, TableHeadText } from '../../../../../components/pdf/TableHelpers'
import Separator from '../../../../../components/pdf/Separator'
import SimplePointsTable from '../../_shared/SimplePointsTable'
import Text, { Bold } from '../../../../../components/pdf/Text'
import View from '../../../../../components/pdf/View'

import {
  remapReplies,
  remapReplyChildren,
  getSeatsReplies,
  filterRepliesByQuestionName
} from '../../_helpers/pdfHelpers'

const EmployeesAndErgoRisksSection = ({ serviceOrder }) => {
  const remap = useMemo(() => remapReplyChildren(serviceOrder.replies), [serviceOrder.replies])

  const temRespostaDeAtendimento = useMemo(() => {
    const replies = filterRepliesByQuestionName(serviceOrder.replies, 'Área de Atendimento')
    const replyFound = replies.find(reply => reply.value)
    return replyFound
  }, [serviceOrder.replies])

  const temRespostaDeComercial = useMemo(() => {
    const replies = filterRepliesByQuestionName(serviceOrder.replies, 'Área Comercial')
    const replyFound = replies.find(reply => reply.value)
    return replyFound
  }, [serviceOrder.replies])

  const data = useMemo(() => {
    const result = remapReplies(serviceOrder.replies, remap)
    return result
  }, [remap, serviceOrder.replies])

  const respostasDeAssentos = useMemo(() => {
    const result = { comercial: true, atendimento: true }
    result.comercial = data.comercial.some(resposta => {
      const respostas = getSeatsReplies(resposta, remap)
      const r1 = respostas[0].rows.some(elem => elem.value !== true)
      const r2 = respostas[1].rows.some(elem => elem.value !== true)

      return r1 || r2
    })

    result.atendimento = data.atendimento.some(resposta => {
      const respostas = getSeatsReplies(resposta, remap)
      const r1 = respostas[0].rows.some(elem => elem.value !== true)
      const r2 = respostas[1].rows.some(elem => elem.value !== true)

      return r1 || r2
    })
    return result
  }, [remap, data.comercial, data.atendimento])

  return (
    <View id="14">
      <Text bold size="small">
        14.DESCRIÇÃO DO CONTINGENTE DE FUNCIONÁRIOS E AVALIAÇÃO DOS RISCOS ERGONÔMICOS
      </Text>

      <Separator weight={8} />

      <Text>
        Para elaboração deste documento adotou-se a definição de Grupo Homogêneo de Exposição (GHE), que corresponde a
        um grupo de trabalhadores que experimentam exposição semelhante de forma que, o resultado fornecido pela
        avaliação da exposição de qualquer trabalhador do grupo seja representativo da exposição do restante dos
        trabalhadores do mesmo grupo.
        <br />
        Um grupo é homogêneo no sentido estatístico, e isso permite que um número relativamente pequeno de amostras
        possa definir as tendências de exposição de todo o grupo.
        <br />A exposição dos trabalhadores não será idêntica, pois quem é homogêneo é o caráter estatístico do grupo, e
        as variabilidades serão normais dentro dele.
      </Text>

      <Separator weight={4} />

      <Text bold size="small" id={'14.1'}>
        14.1. DESCRIÇÃO DO CONTINGENTE DE FUNCIONÁRIOS
      </Text>

      <Separator weight={4} />

      <Text>
        Como forma de mapeamento da população da empresa, serão levantados os aspectos de distribuição de grupos
        setoriais. Todas as informações relacionadas ao número de funcionários estão baseadas no cadastro referente à
        data da avaliação, podendo, portanto, ser alteradas a qualquer momento de acordo com as necessidades da empresa.
      </Text>

      <Separator weight={4} />

      <Text bold size="small" id={'14.2'}>
        14.2. AVALIAÇÃO DOS RISCOS ERGONÔMICOS
      </Text>

      <Separator weight={4} />

      <Text>
        Para o estudo e aprofundamento de possíveis riscos ergonômicos pontuados no PGR através da AEP para os
        colaboradores da empresa serão considerados os seguintes critérios:
      </Text>

      <ul>
        <li>
          <Text>Avaliação Simplificada da Exigência para a Coluna Vertebral pelos Esforços Efetuados no Trabalho;</Text>
        </li>
        <li>
          <Text>Avaliação Ergonômica Geral para Fatores Biomecânicos dos Membros Superiores;</Text>
        </li>
      </ul>

      <Separator weight={4} />

      <Text>Além desses pontos são considerados para a caracterização dos riscos:</Text>

      <ul>
        <li>
          <Text>Assentos;</Text>
        </li>
        <li>
          <Text>Mesas;</Text>
        </li>
        <li>
          <Text>Computadores e acessórios;</Text>
        </li>
      </ul>

      <Separator weight={4} />
      {temRespostaDeAtendimento && (
        <>
          <Text>
            <Bold>SETOR: </Bold> ATENDIMENTO
          </Text>

          <Separator weight={4} />

          <Table width="100%">
            <TableRow>
              <TableColText colSpan={2}>
                <Bold>Jornada: </Bold> 6 horas e 8 horas
                <br />
                <Bold>Pausa: </Bold> 1 hora para almoço e pausas indefinidas para toalete e água
                <br />
                <Bold>Tarefa / Atividade</Bold>
                <br />
                <Bold>(X) Tarefa única composta por mais de uma atividade</Bold>
                <br />
                {'(  )'} Múltiplas tarefas e atividades
                <br />
                {'(  )'} Tarefa única composta por única atividade
                <br />
                <Bold>Posto de Trabalho</Bold>
                <br />
                Ambiente escritório padrão (mesa tampo único, desktop, cadeira)
              </TableColText>
            </TableRow>

            <TableRow>
              <TableHeadText align="center">Item</TableHeadText>
              <TableHeadText align="center">Avaliação</TableHeadText>
            </TableRow>

            <TableRow>
              <TableColText>Cadeira</TableColText>
              <TableColText align="center">{respostasDeAssentos.atendimento ? 'Inadequado' : 'Adequado'}</TableColText>
            </TableRow>

            <TableRow>
              <TableColText>Mesa</TableColText>
              <TableColText align="center">Adequado</TableColText>
            </TableRow>

            <TableRow>
              <TableColText>Computador (Mouse / Teclado / Monitor) (FIXO)</TableColText>
              <TableColText align="center">Adequado</TableColText>
            </TableRow>

            <TableRow>
              <TableColText>Organização do Trabalho</TableColText>
              <TableColText align="center">Adequado</TableColText>
            </TableRow>

            <TableRow>
              <TableColText>Guarda Documentos</TableColText>
              <TableColText align="center">Adequado</TableColText>
            </TableRow>

            <TableRow>
              <TableColText>Circulação</TableColText>
              <TableColText align="center">Adequado</TableColText>
            </TableRow>
          </Table>

          <Separator weight={12} />

          <Text bold size="small">
            AVALIAÇÃO SIMPLIFICADA – COLUNA VERTEBRAL
          </Text>

          <Separator weight={4} />

          <Text>
            Avaliação Simplificada da Exigência para a Coluna Vertebral pelos Esforços Efetuados no Trabalho
            <br />
            Autores: Hudson Couto e Remi Lopes Antônio
          </Text>

          <Separator weight={4} />

          <SimplePointsTable
            sections={[
              {
                label: '1. Avaliação de situações extremas',
                footer:
                  'Na existência de alguma dessas situações, caracteriza-se situação de alta exigência para a coluna vertebral, independente da frequência ou da taxa de ocupação;',
                rows: [
                  {
                    label: 'Esforços extremos evidenciados por técnicas qualitativas ou quantitativas',
                    value: false
                  },
                  {
                    label: 'Cargas levantadas totalmente por uma só pessoa de peso maior que 25 kg',
                    value: false
                  }
                ]
              },
              {
                label: 'Avaliação de situações não extremas',
                categories: [
                  {
                    label: '1. Postura Estática',
                    rows: [
                      {
                        label:
                          'O trabalho exige posicionamento estático do tronco em posição encurvada para frente, mesmo que em pequeno grau de flexão?',
                        value: false,
                        points: 1
                      },
                      {
                        label: 'Ações técnicas dinâmicas dos membros superiores com esforço estático do tronco',
                        value: false,
                        points: 1
                      },
                      {
                        label: 'Ações técnicas predominantemente estáticas dos membros',
                        value: false,
                        points: 1
                      },
                      {
                        label: 'Superiores com esforço estático do tronco',
                        value: false,
                        points: 2
                      },
                      {
                        label: 'Posicionamento estático do tronco por mais que 50% do ciclo ou da tarefa',
                        value: false,
                        points: 4
                      },
                      {
                        label: 'Há esforços físicos de alta intensidade dos braços ou de todo o corpo nessa posição?',
                        value: false,
                        points: 1
                      }
                    ]
                  },
                  {
                    label: '2. Movimentação de Materiais',
                    rows: [
                      {
                        label: 'Há necessidade de levantar totalmente ou colocar com precaução alguma carga?',
                        value: false,
                        points: 1 // Parece que é variavel para esse caso
                      },
                      { label: 'Fatores Agravantes', separator: true },
                      {
                        label: 'Frequência maior que uma vez a cada 5 minutos',
                        value: false,
                        points: 1
                      },
                      {
                        label: 'Frequência maior que 4 vezes por minuto',
                        value: false,
                        points: 2
                      },
                      {
                        label: 'Frequência maior que 8 vezes por minuto',
                        value: false,
                        points: 5
                      },
                      {
                        label: 'Esforço feito longe do corpo',
                        value: false,
                        points: 2
                      },
                      {
                        label:
                          'Em posição assimétrica do tronco ou tendo que fazer o esforço ao lado do corpo com uma só mão',
                        value: false,
                        points: 1
                      },
                      {
                        label: 'Abaixo do nível dos joelhos',
                        value: false,
                        points: 1
                      },
                      {
                        label: 'Acima do nível da cabeça',
                        value: false,
                        points: 1
                      },
                      {
                        label:
                          'Pega ruim e/ou distância vertical entre o ponto inicial e o ponto final do esforço > 50 cm',
                        value: false,
                        points: 1
                      }
                    ]
                  },
                  {
                    label: '3. Empurrar ou Puxar',
                    rows: [
                      {
                        label: 'Há necessidade de empurrar ou puxar carrinhos ou transpaleteiras manuais',
                        value: false,
                        points: 1
                      },
                      {
                        label: 'Peso da carga maior que 700 kg',
                        value: false,
                        points: 2
                      },
                      {
                        label: 'Condições ruins de piso',
                        value: false,
                        points: 1
                      },
                      {
                        label: 'Condições ruins do equipamento exigindo maior esforço',
                        value: false,
                        points: 1
                      },
                      {
                        label: 'Esforço mais frequente que 12 vezes/ hora?',
                        value: false,
                        points: 1
                      }
                    ]
                  },
                  {
                    label: '4. Outros Esforços ou Posicionamento',
                    rows: [
                      {
                        label:
                          'Há algum esforço nítido para a coluna vertebral e tronco que não os citados nos itens 2 e 3?',
                        value: false,
                        points: 3
                      }
                    ]
                  }
                ]
              }
            ]}
            classifications={[
              { min: 0, max: 2, label: 'Baixa exigência para a coluna vertebral' },
              { min: 3, max: 5, label: 'Posto de trabalho de média exigência para a coluna vertebral' },
              { min: 6, max: 9, label: 'Posto de trabalho de alta exigência para a coluna vertebral' },
              { min: 9, label: 'Posto de trabalho de altíssima exigência para a coluna vertebral' }
            ]}
          />

          <Separator weight={12} />

          <Text bold size="small">
            AVALIAÇÃO DOS FATORES BIOMECÂNICOS DOS MEMBROS SUPERIORES
          </Text>

          <Separator weight={4} />

          <Text>
            Avaliação Ergonômica Geral para Fatores Biomecânicos dos Membros Superiores
            <br />
            Autores: Hudson Couto e Remi Lopes Antônio
          </Text>

          <Separator weight={4} />

          <SimplePointsTable
            sections={[
              {
                label: 'Sobrecarga Física',
                rows: [
                  {
                    label:
                      'Há contato da mão ou punho ou tecidos moles com alguma quina viva de objetos ou ferramentas?',
                    value: false,
                    points: 1
                  },
                  {
                    label: 'O trabalho exige o uso de ferramentas vibratórias?',
                    value: false,
                    points: 1
                  },
                  {
                    label: 'O trabalho é feito em condições ambientais de frio excessivo?',
                    value: false,
                    points: 1
                  },
                  {
                    label:
                      'Há necessidade do uso de luvas e, em consequência disso, o trabalhador tem que fazer mais força?',
                    value: false,
                    points: 1
                  },
                  {
                    label: 'O trabalhador tem que movimentar peso acima de 300 g, como rotina em sua atividade?',
                    value: false,
                    points: 1
                  }
                ]
              },
              {
                label: 'Força com as Mãos',
                rows: [
                  {
                    label: 'Aparentemente as mãos têm que fazer muita força?',
                    value: false,
                    points: 1
                  },
                  {
                    label: 'A posição de pinça (pulpar, lateral ou palmar) é utilizada para fazer força?',
                    value: false,
                    points: 1
                  },
                  {
                    label:
                      'Quando usados para apertar botões, teclas ou componentes, para montar ou inserir, ou para exercer compressão digital, a força de compressão exercida pelos dedos ou pela mão é de alta intensidade?',
                    value: false,
                    points: 1
                  },
                  {
                    label:
                      'O esforço manual detectado é feito durante mais que 49% do ciclo ou é repetido mais que 8 vezes por minuto?',
                    value: false,
                    points: 1
                  }
                ]
              },
              {
                label: 'Postura',
                rows: [
                  {
                    label: 'Há algum esforço estático da mão ou do antebraço como rotina na realização do trabalho?',
                    value: false,
                    points: 1
                  },
                  {
                    label: 'Há algum esforço estático do braço ou do pescoço como rotina na realização do trabalho?',
                    value: false,
                    points: 1
                  },
                  {
                    label: 'Há extensão ou flexão forçada do punho como rotina na execução da tarefa?',
                    value: false,
                    points: 1
                  },
                  {
                    label: 'Há desvio ulnar ou radial forçado do punho como rotina na execução da tarefa?',
                    value: false,
                    points: 1
                  },
                  {
                    label:
                      'Há abdução do braço acima de 45 graus ou elevação dos braços acima do nível dos ombros como rotina na execução da tarefa?',
                    value: false,
                    points: 1
                  },
                  {
                    label: 'Ha outras posturas forçadas dos membros superiores?',
                    value: false,
                    points: 1
                  },
                  {
                    label: 'O trabalhador tem inflexibilidade na sua postura durante a jornada?',
                    value: false,
                    points: 1
                  }
                ]
              },
              {
                label: 'Posto de Trabalho e Esforço Estático',
                rows: [
                  {
                    label:
                      'A atividade é de alta precisão de movimentos? Ou existe alguma contração muscular para estabilizar uma parte do corpo enquanto outra parte executa o trabalho?',
                    value: false,
                    points: 1
                  },
                  {
                    label: 'A altura do posto de trabalho não possui regulagem?',
                    value: respostasDeAssentos.atendimento,
                    points: 1
                  }
                ]
              },
              {
                label: 'Repetitividade e Organização do Trabalho',
                rows: [
                  {
                    label:
                      'Existe algum tipo de movimento que é repetido por mais de 3.000 vezes no turno? Ou o ciclo é menor que 30 segundos, sem pausa curtíssima de 15% ou mais do mesmo?',
                    value: false,
                    points: 1
                  },
                  {
                    label:
                      'No caso de ciclo maior que 30 segundos, não ocorre variação de movimento de modo que o padrão de movimento permaneça o mesmo ao longo de 50% do ciclo?',
                    value: false,
                    points: 1
                  },
                  {
                    label:
                      'Não ocorre revezamento nas tarefas de modo que ocorra a alternância de grupamentos musculares?',
                    value: false,
                    points: 1
                  },
                  {
                    label: 'Percebem-se sinais de estar o trabalhador com o tempo apertado para realizar sua tarefa?',
                    value: false,
                    points: 1
                  },
                  {
                    label:
                      'Entre um ciclo e outro não há a possibilidade de um pequeno descanso? Ou não há pausa bem definida de aproximadamente 5 a 10 minutos por hora?',
                    value: false,
                    points: 1
                  }
                ]
              }
            ]}
            classifications={[
              { min: 0, max: 3, label: 'Ausência de fatores biomecânicos' },
              { min: 4, max: 6, label: 'Fator biomecânico pouco significativo- AUSÊNCIA DE RISCO' },
              { min: 7, max: 9, label: 'Fator biomecânico de moderada importância- IMPROVÁVEL, MAS POSSÍVEL' },
              { min: 10, max: 14, label: 'Fator biomecânico significativo- RISCO' },
              { min: 15, label: 'Fator biomecânico muito significativo- ALTO RISCO' }
            ]}
          />

          <Separator weight={8} />
        </>
      )}
      <Separator weight={8} />

      {temRespostaDeComercial && (
        <>
          <Text>
            <Bold>SETOR: </Bold> COMERCIAL
          </Text>

          <Separator weight={4} />

          <Table width="100%">
            <TableRow>
              <TableColText colSpan={2}>
                <Bold>Jornada: </Bold> 8 horas
                <br />
                <Bold>Pausa: </Bold> 1 hora para almoço e pausas indefinidas para toalete e água
                <br />
                <Bold>Tarefa / Atividade</Bold>
                <br />
                <Bold>(X) Tarefa única composta por mais de uma atividade</Bold>
                <br />
                ( . ) Múltiplas tarefas e atividades
                <br />
                ( . ) Tarefa única composta por única atividade
                <br />
                <Bold>Posto de Trabalho</Bold>
                <br />
                Ambiente escritório padrão (mesa tampo único, desktop, cadeira)
              </TableColText>
            </TableRow>

            <TableRow>
              <TableHeadText align="center">Item</TableHeadText>
              <TableHeadText align="center">Avaliação</TableHeadText>
            </TableRow>

            <TableRow>
              <TableColText>Cadeira</TableColText>
              <TableColText align="center">{respostasDeAssentos.comercial ? 'Inadequado' : 'Adequado'}</TableColText>
            </TableRow>

            <TableRow>
              <TableColText>Mesa</TableColText>
              <TableColText align="center">Adequado</TableColText>
            </TableRow>

            <TableRow>
              <TableColText>Computador (Mouse / Teclado / Monitor) (FIXO)</TableColText>
              <TableColText align="center">Adequado</TableColText>
            </TableRow>

            <TableRow>
              <TableColText>Organização do Trabalho</TableColText>
              <TableColText align="center">Adequado</TableColText>
            </TableRow>

            <TableRow>
              <TableColText>Guarda Documentos</TableColText>
              <TableColText align="center">Adequado</TableColText>
            </TableRow>

            <TableRow>
              <TableColText>Circulação</TableColText>
              <TableColText align="center">Adequado</TableColText>
            </TableRow>
          </Table>

          <Separator weight={12} />

          <Text bold size="small">
            AVALIAÇÃO SIMPLIFICADA – COLUNA VERTEBRAL
          </Text>

          <Separator weight={4} />

          <Text>
            Avaliação Simplificada da Exigência para a Coluna Vertebral pelos Esforços Efetuados no Trabalho
            <br />
            Autores: Hudson Couto e Remi Lopes Antônio
          </Text>

          <Separator weight={4} />

          <SimplePointsTable
            sections={[
              {
                label: '1. Avaliação de situações extremas',
                footer:
                  'Na existência de alguma dessas situações, caracteriza-se situação de alta exigência para a coluna vertebral, independente da frequência ou da taxa de ocupação;',
                rows: [
                  {
                    label: 'Esforços extremos evidenciados por técnicas qualitativas ou quantitativas',
                    value: false
                  },
                  {
                    label: 'Cargas levantadas totalmente por uma só pessoa de peso maior que 25 kg',
                    value: false
                  }
                ]
              },
              {
                label: 'Avaliação de situações não extremas',
                categories: [
                  {
                    label: '1. Postura Estática',
                    rows: [
                      {
                        label:
                          'O trabalho exige posicionamento estático do tronco em posição encurvada para frente, mesmo que em pequeno grau de flexão?',
                        value: false,
                        points: 1
                      },
                      {
                        label: 'Ações técnicas dinâmicas dos membros superiores com esforço estático do tronco',
                        value: false,
                        points: 1
                      },
                      {
                        label: 'Ações técnicas predominantemente estáticas dos membros',
                        value: false,
                        points: 1
                      },
                      {
                        label: 'Superiores com esforço estático do tronco',
                        value: false,
                        points: 2
                      },
                      {
                        label: 'Posicionamento estático do tronco por mais que 50% do ciclo ou da tarefa',
                        value: false,
                        points: 4
                      },
                      {
                        label: 'Há esforços físicos de alta intensidade dos braços ou de todo o corpo nessa posição?',
                        value: false,
                        points: 1
                      }
                    ]
                  },
                  {
                    label: '2. Movimentação de Materiais',
                    rows: [
                      {
                        label: 'Há necessidade de levantar totalmente ou colocar com precaução alguma carga?',
                        value: false,
                        points: 1 // Parece que é variavel para esse caso
                      },
                      { label: 'Fatores Agravantes', separator: true },
                      {
                        label: 'Frequência maior que uma vez a cada 5 minutos',
                        value: false,
                        points: 1
                      },
                      {
                        label: 'Frequência maior que 4 vezes por minuto',
                        value: false,
                        points: 2
                      },
                      {
                        label: 'Frequência maior que 8 vezes por minuto',
                        value: false,
                        points: 5
                      },
                      {
                        label: 'Esforço feito longe do corpo',
                        value: false,
                        points: 2
                      },
                      {
                        label:
                          'Em posição assimétrica do tronco ou tendo que fazer o esforço ao lado do corpo com uma só mão',
                        value: false,
                        points: 1
                      },
                      {
                        label: 'Abaixo do nível dos joelhos',
                        value: false,
                        points: 1
                      },
                      {
                        label: 'Acima do nível da cabeça',
                        value: false,
                        points: 1
                      },
                      {
                        label:
                          'Pega ruim e/ou distância vertical entre o ponto inicial e o ponto final do esforço > 50 cm',
                        value: false,
                        points: 1
                      }
                    ]
                  },
                  {
                    label: '3. Empurrar ou Puxar',
                    rows: [
                      {
                        label: 'Há necessidade de empurrar ou puxar carrinhos ou transpaleteiras manuais',
                        value: false,
                        points: 1
                      },
                      {
                        label: 'Peso da carga maior que 700 kg',
                        value: false,
                        points: 2
                      },
                      {
                        label: 'Condições ruins de piso',
                        value: false,
                        points: 1
                      },
                      {
                        label: 'Condições ruins do equipamento exigindo maior esforço',
                        value: false,
                        points: 1
                      },
                      {
                        label: 'Esforço mais frequente que 12 vezes/ hora?',
                        value: false,
                        points: 1
                      }
                    ]
                  },
                  {
                    label: '4. Outros Esforços ou Posicionamento',
                    rows: [
                      {
                        label:
                          'Há algum esforço nítido para a coluna vertebral e tronco que não os citados nos itens 2 e 3?',
                        value: false,
                        points: 3
                      }
                    ]
                  }
                ]
              }
            ]}
            classifications={[
              { min: 0, max: 2, label: 'Baixa exigência para a coluna vertebral' },
              { min: 3, max: 5, label: 'Posto de trabalho de média exigência para a coluna vertebral' },
              { min: 6, max: 9, label: 'Posto de trabalho de alta exigência para a coluna vertebral' },
              { min: 9, label: 'Posto de trabalho de altíssima exigência para a coluna vertebral' }
            ]}
          />
          <Separator weight={12} />

          <Text bold size="small">
            AVALIAÇÃO DOS FATORES BIOMECÂNICOS DOS MEMBROS SUPERIORES
          </Text>

          <Separator weight={4} />

          <Text>
            Avaliação Ergonômica Geral para Fatores Biomecânicos dos Membros Superiores
            <br />
            Autores: Hudson Couto e Remi Lopes Antônio
          </Text>

          <Separator weight={4} />

          <SimplePointsTable
            sections={[
              {
                label: 'Sobrecarga Física',
                rows: [
                  {
                    label:
                      'Há contato da mão ou punho ou tecidos moles com alguma quina viva de objetos ou ferramentas?',
                    value: false,
                    points: 1
                  },
                  {
                    label: 'O trabalho exige o uso de ferramentas vibratórias?',
                    value: false,
                    points: 1
                  },
                  {
                    label: 'O trabalho é feito em condições ambientais de frio excessivo?',
                    value: false,
                    points: 1
                  },
                  {
                    label:
                      'Há necessidade do uso de luvas e, em consequência disso, o trabalhador tem que fazer mais força?',
                    value: false,
                    points: 1
                  },
                  {
                    label: 'O trabalhador tem que movimentar peso acima de 300 g, como rotina em sua atividade?',
                    value: false,
                    points: 1
                  }
                ]
              },
              {
                label: 'Força com as Mãos',
                rows: [
                  {
                    label: 'Aparentemente as mãos têm que fazer muita força?',
                    value: false,
                    points: 1
                  },
                  {
                    label: 'A posição de pinça (pulpar, lateral ou palmar) é utilizada para fazer força?',
                    value: false,
                    points: 1
                  },
                  {
                    label:
                      'Quando usados para apertar botões, teclas ou componentes, para montar ou inserir, ou para exercer compressão digital, a força de compressão exercida pelos dedos ou pela mão é de alta intensidade?',
                    value: false,
                    points: 1
                  },
                  {
                    label:
                      'O esforço manual detectado é feito durante mais que 49% do ciclo ou é repetido mais que 8 vezes por minuto?',
                    value: false,
                    points: 1
                  }
                ]
              },
              {
                label: 'Postura',
                rows: [
                  {
                    label: 'Há algum esforço estático da mão ou do antebraço como rotina na realização do trabalho?',
                    value: false,
                    points: 1
                  },
                  {
                    label: 'Há algum esforço estático do braço ou do pescoço como rotina na realização do trabalho?',
                    value: false,
                    points: 1
                  },
                  {
                    label: 'Há extensão ou flexão forçada do punho como rotina na execução da tarefa?',
                    value: false,
                    points: 1
                  },
                  {
                    label: 'Há desvio ulnar ou radial forçado do punho como rotina na execução da tarefa?',
                    value: false,
                    points: 1
                  },
                  {
                    label:
                      'Há abdução do braço acima de 45 graus ou elevação dos braços acima do nível dos ombros como rotina na execução da tarefa?',
                    value: false,
                    points: 1
                  },
                  {
                    label: 'Ha outras posturas forçadas dos membros superiores?',
                    value: false,
                    points: 1
                  },
                  {
                    label: 'O trabalhador tem inflexibilidade na sua postura durante a jornada?',
                    value: false,
                    points: 1
                  }
                ]
              },
              {
                label: 'Posto de Trabalho e Esforço Estático',
                rows: [
                  {
                    label:
                      'A atividade é de alta precisão de movimentos? Ou existe alguma contração muscular para estabilizar uma parte do corpo enquanto outra parte executa o trabalho?',
                    value: false,
                    points: 1
                  },
                  {
                    label: 'A altura do posto de trabalho não possui regulagem?',
                    value: respostasDeAssentos.comercial,
                    points: 1
                  }
                ]
              },
              {
                label: 'Repetitividade e Organização do Trabalho',
                rows: [
                  {
                    label:
                      'Existe algum tipo de movimento que é repetido por mais de 3.000 vezes no turno? Ou o ciclo é menor que 30 segundos, sem pausa curtíssima de 15% ou mais do mesmo?',
                    value: false,
                    points: 1
                  },
                  {
                    label:
                      'No caso de ciclo maior que 30 segundos, não ocorre variação de movimento de modo que o padrão de movimento permaneça o mesmo ao longo de 50% do ciclo?',
                    value: false,
                    points: 1
                  },
                  {
                    label:
                      'Não ocorre revezamento nas tarefas de modo que ocorra a alternância de grupamentos musculares?',
                    value: false,
                    points: 1
                  },
                  {
                    label: 'Percebem-se sinais de estar o trabalhador com o tempo apertado para realizar sua tarefa?',
                    value: false,
                    points: 1
                  },
                  {
                    label:
                      'Entre um ciclo e outro não há a possibilidade de um pequeno descanso? Ou não há pausa bem definida de aproximadamente 5 a 10 minutos por hora?',
                    value: false,
                    points: 1
                  }
                ]
              }
            ]}
            classifications={[
              { min: 0, max: 3, label: 'Ausência de fatores biomecânicos' },
              { min: 4, max: 6, label: 'Fator biomecânico pouco significativo- AUSÊNCIA DE RISCO' },
              { min: 7, max: 9, label: 'Fator biomecânico de moderada importância- IMPROVÁVEL, MAS POSSÍVEL' },
              { min: 10, max: 14, label: 'Fator biomecânico significativo- RISCO' },
              { min: 15, label: 'Fator biomecânico muito significativo- ALTO RISCO' }
            ]}
          />

          <Separator weight={8} />
        </>
      )}
    </View>
  )
}

export default EmployeesAndErgoRisksSection
